// src/expenseExplorer.js
// ─────────────────────────────────────────────────────────────
// Expenses → "What it went on", the drill-down explorer (10/10/26). Pure —
// no React, no network; src/views/Expenses.jsx draws it.
//
// Reece: "I want to have the 'what it went on' be the very high level
// categories like 'Marketing' 'Software' 'Hardware' and then once I click it
// into it, then bringing up the subcategories of that expense, and fading
// the other ones, then give an option to click back, and have it be an
// interactive screen that can populate the things as I'm clicking through
// or filtering".
//
// Every expense is split into PARTS by src/expenseItems.js
// (expenseItemParts — a mixed receipt by its "Items:" line, to the cent;
// any other row is one part for its whole amount). Each part sits at
// exactly one tile on every level, so a level's tiles always add up to
// their parent to the cent:
//   Level 1 — the group (display only; stored categories never change):
//     Marketing, Software (Software/Subscriptions), Hardware (the
//     Office/Equipment parts whose item type is Equipment — computers,
//     printers, monitors, phone hardware, furniture), Office Supplies (the
//     other Office/Equipment parts), Travel & Meals, Licensing & Education,
//     Other (Other + any category the app doesn't know).
//   Level 2 — Marketing: its subcategories (Data, Postage, Paper, …, Other
//     marketing); Software: the named software item (Claude, RingCentral,
//     Adobe, Wispr Flow…), else the vendor; the rest: item types when the
//     group's parts in view are of two or more ("Paper", "Ink & Toner",
//     "No item detail", "Other items"), else vendors.
//   Level 3 — vendor groups (Amazon, Staples, …) for that tile — for Data,
//     the WEBSITES he buys it from (PropertyShark, PropStream, PropRadar,
//     DataZapp…). (10/10/26) For Paper, Envelopes, Cardstock, Ink & Toner,
//     Business Cards and Labels, level 3 is the VARIETY first — Reece:
//     "standard paper, 28 lb. paper, versus the standard envelopes, versus
//     the more expensive Southworth envelopes" — one tile per kind ($,
//     purchases, units, cost per sheet / envelope / card when every
//     dollar's count is known; "No type detail" for parts that don't say),
//     then level 4 = the stores for that kind, with their unit costs.
//     (10/10/26, later — Reece: "for the paper types, you can just break it
//     down to Southworth paper versus non-Southworth paper. That would likely
//     be sufficient to get an idea of the cost.") Paper, Envelopes and
//     Cardstock open on the BRAND first: "Southworth" vs "Non-Southworth"
//     ("No brand detail" for parts with nothing to judge — never a guess),
//     each with $, purchases, units and the cost per sheet / envelope when
//     every dollar's count is known; under each, its kinds, then the stores
//     (level 5). Data that names no brand draws exactly as before.
// A path entry is {by, key, label}; `by` (group | sub | software | type |
// brand | variety | vendor) is stored with it, so a path keeps meaning the same
// parts when a timeline or search change would pick another dimension for
// that level. The path IS the page's item filter (partMatchesPath).
// ─────────────────────────────────────────────────────────────

import { expenseItemParts, marketingSubOf, unitCostLabel, unitsLabel, hasVarieties, isOtherVariety, varietyNote, hasBrand, brandKeyOf, brandRank, brandNote, NAMED_SOFTWARE, NO_VENDOR, ITEM_OTHER, NO_DETAIL, OTHER_MARKETING, NO_TYPE_DETAIL, NO_BRAND_DETAIL } from './expenseItems';

var GROUPS=["Marketing","Software","Hardware","Office Supplies","Travel & Meals","Licensing & Education","Other"];
var CAT_GROUP={"Marketing":"Marketing","Software/Subscriptions":"Software","Travel & Meals":"Travel & Meals","Licensing & Education":"Licensing & Education","Other":"Other"};
// What each group reads, for a tile's tooltip.
var GROUP_NOTE={
  "Marketing":"everything filed under Marketing",
  "Software":"Software/Subscriptions",
  "Hardware":"Office/Equipment — the equipment (computers, printers, monitors, phones, furniture)",
  "Office Supplies":"Office/Equipment — everything that isn't equipment",
  "Travel & Meals":"Travel & Meals",
  "Licensing & Education":"Licensing & Education",
  "Other":"Other (and anything without a category)"
};
var MAX_DEPTH=5;
var NAMED={};NAMED_SOFTWARE.forEach(function(r){NAMED[r[1]]=true;});
// The catch-all tiles sort after the real ones.
// (an "Other paper" kind next to last, "No type detail" / "No item detail"
// / "No vendor" / "No brand detail" last)
var CATCH_ALL={};[OTHER_MARKETING,ITEM_OTHER].forEach(function(k){CATCH_ALL[k]=1;});[NO_DETAIL,NO_VENDOR,NO_TYPE_DETAIL,NO_BRAND_DETAIL].forEach(function(k){CATCH_ALL[k]=2;});
function catchRank(by,key){return CATCH_ALL[key]||(by==="variety"&&isOtherVariety(key)?1:0);}
var OTHER_ITEMS="Other items";

// ── Which tile a part sits on ────────────────────────────────

// The level-1 group of one part of an expense. Only Office/Equipment is
// split by part: a receipt with a monitor and a box of pens is Hardware AND
// Office Supplies, each with its own dollars.
function groupOfPart(ex,p){
  var cat=ex&&ex.cat;
  if(cat==="Office/Equipment")return p&&p.type==="Equipment"?"Hardware":"Office Supplies";
  return CAT_GROUP[cat]||"Other";
}
// Software: a named software item ("Claude"), else the vendor, else what
// the row says ("Monthly subscription").
function softwareKey(p){
  if(p&&NAMED[p.type])return p.type;
  if(p&&p.vendor&&p.vendor!==NO_VENDOR)return p.vendor;
  return(p&&p.label)||"Software";
}
function keyOf(by,ex,p){
  if(by==="group")return groupOfPart(ex,p);
  if(by==="sub")return(p&&p.sub)||marketingSubOf(p&&p.type);
  if(by==="software")return softwareKey(p);
  if(by==="type")return(p&&p.row)||ITEM_OTHER;
  if(by==="vendor")return(p&&p.vendor)||NO_VENDOR;
  if(by==="variety")return(p&&hasVarieties(p.type)&&p.variety)||NO_TYPE_DETAIL;
  if(by==="brand")return brandKeyOf(p);
  return null;
}
function labelOf(by,key){return by==="type"&&key===ITEM_OTHER?OTHER_ITEMS:key;}

// Does this part of this expense sit under the path?
function partMatchesPath(ex,p,path){
  for(var i=0;i<(path||[]).length;i++){var e=path[i];if(!e||keyOf(e.by,ex,p)!==e.key)return false;}
  return true;
}
// …and the expense: one of its parts does.
function rowMatchesPath(ex,path,partsOf){
  if(!path||!path.length)return true;
  return(partsOf||expenseItemParts)(ex).some(function(p){return partMatchesPath(ex,p,path);});
}

// The dimension a level is drawn in. `items` = the parts under the path.
function dimFor(depth,path,items){
  if(depth===0)return"group";
  if(depth===1){
    var g=path[0]&&path[0].key;
    if(g==="Marketing")return"sub";
    if(g==="Software")return"software";
    var rows={},n=0;
    (items||[]).forEach(function(x){var r=keyOf("type",x.ex,x.p);if(!rows[r]){rows[r]=true;n++;}});
    return n>=2?"type":"vendor";
  }
  var anyKind=function(){return(items||[]).some(function(x){return!!(x.p&&hasVarieties(x.p.type)&&x.p.variety);});};
  if(depth===2){
    // Paper / Envelopes / Cardstock: Southworth or not first — when any
    // part in view has a brand; then (or for the other supplies with kinds)
    // its kinds, then the stores — when any part in view says its kind
    // (otherwise the one "No brand detail" / "No type detail" tile would
    // only be a click in the way)
    var t=path[1];
    var supply=t&&(t.by==="sub"||t.by==="type");
    if(supply&&hasBrand(t.key)&&(items||[]).some(function(x){return!!(x.p&&hasBrand(x.p.type)&&x.p.brand);}))return"brand";
    return supply&&hasVarieties(t.key)&&anyKind()?"variety":"vendor";
  }
  if(depth===3){
    if(path[2]&&path[2].by==="brand")return anyKind()?"variety":"vendor";
    return path[2]&&path[2].by==="variety"?"vendor":null;
  }
  if(depth===4)return path[2]&&path[2].by==="brand"&&path[3]&&path[3].by==="variety"?"vendor":null;
  return null;
}
function bucket(by,items){
  var m=new Map();
  items.forEach(function(x){var k=keyOf(by,x.ex,x.p);var a=m.get(k);if(!a){a=[];m.set(k,a);}a.push(x);});
  return m;
}
// Is there anything to drill into below `path`: some deeper level with two
// or more tiles? (A single tile all the way down is not worth a click —
// "Software › Claude" is all Anthropic.)
function splitsBelow(depth,path,items){
  if(depth>=MAX_DEPTH||!items.length)return false;
  var by=dimFor(depth,path,items);
  var m=bucket(by,items);
  if(m.size>=2)return true;
  var k=m.keys().next().value;
  return splitsBelow(depth+1,path.concat([{by:by,key:k,label:labelOf(by,k)}]),m.get(k));
}

// ── A level of tiles ─────────────────────────────────────────

function newCell(){return{cents:0,ids:new Set(),units:0,unitsKnown:true,unitName:null,types:new Set()};}
// The same rule as "Where you buy it": a unit cost only when the count
// behind every dollar is known (a refund with a count takes its units off)
// — and only for a tile of ONE item type (sheets of paper and sheets of
// cardstock are not one price).
function addToCell(cell,ex,p){
  cell.cents+=p.cents;cell.ids.add(ex);cell.types.add(p.type||null);
  if(p.unitName&&!cell.unitName)cell.unitName=p.unitName;
  if(p.units!=null)cell.units+=(p.cents<0?-1:1)*p.units;
  else if(p.cents)cell.unitsKnown=false;
}
function unitOf(cell){
  var one=cell.types.size===1&&!cell.types.has(null);
  if(!one||!cell.unitName||!cell.unitsKnown||!(cell.units>0)||!(cell.cents>0))return null;
  return cell.cents/100/cell.units;
}
function pctOf(c,parent){return parent?Math.round(c/parent*1000)/10:0;}
function tileOrder(by){
  if(by==="group")return function(a,b){return GROUPS.indexOf(a.key)-GROUPS.indexOf(b.key);};
  if(by==="brand")return function(a,b){return brandRank(a.key)-brandRank(b.key);};   // Southworth, Non-Southworth, No brand detail
  return function(a,b){return catchRank(by,a.key)-catchRank(by,b.key)||b.cents-a.cents||String(a.label).localeCompare(String(b.label));};
}

// The tiles one level below `path`, over these expenses →
//   {depth, by, path, cents, amount, n, splits, tiles:[{key, label, by,
//    cents, amount, n, pct, units, unitName, unitsLabel, unitCost,
//    unitCostLabel, note, drillable, path}]}
// cents / n = everything under the path (its total and purchases); every
// part under it is on exactly one tile, so the tiles add up to `cents` to
// the cent. pct = the tile's share of that (one decimal). `drillable` =
// some level below the tile has two or more tiles. opts.by forces the
// dimension (the siblings of a selected tile, drawn the way it was picked).
// partsOf: a cached expenseItemParts (Expenses passes its own, and a
// narrower one while a "Where you buy it" cell is on).
function explorerLevel(list,path,partsOf,opts){
  path=path||[];opts=opts||{};partsOf=partsOf||expenseItemParts;
  var depth=path.length;
  var items=[];var parentC=0;var ids=new Set();
  (list||[]).forEach(function(ex){
    if(!ex)return;
    partsOf(ex).forEach(function(p){if(partMatchesPath(ex,p,path)){items.push({ex:ex,p:p});parentC+=p.cents;ids.add(ex);}});
  });
  var by=depth<MAX_DEPTH?(opts.by||dimFor(depth,path,items)):null;
  var tiles=[];
  if(by){
    bucket(by,items).forEach(function(its,key){
      var cell=newCell();its.forEach(function(x){addToCell(cell,x.ex,x.p);});
      var label=labelOf(by,key);
      var child=path.concat([{by:by,key:key,label:label}]);
      var uc=unitOf(cell);
      var vt=(by==="variety"||by==="brand")&&path[1]?path[1].key:null;
      // a kind (or a brand) bought at ONE store — the tile says where
      var stores=vt?new Set(its.map(function(x){return keyOf("vendor",x.ex,x.p);})):null;
      tiles.push({key:key,label:label,by:by,cents:cell.cents,amount:cell.cents/100,n:cell.ids.size,pct:pctOf(cell.cents,parentC),
        units:uc?cell.units:null,unitName:uc?cell.unitName:null,unitsLabel:uc?unitsLabel(cell.units,cell.unitName):"",unitCost:uc,unitCostLabel:uc?unitCostLabel(uc,cell.unitName):"",
        note:by==="brand"?brandNote(key):vt?(key===NO_TYPE_DETAIL?"purchases whose lines don't say which kind":varietyNote(vt,key)):"",
        onlyStore:stores&&stores.size===1?stores.values().next().value:null,
        drillable:splitsBelow(depth+1,child,its),path:child});
    });
    tiles.sort(tileOrder(by));
  }
  return{depth:depth,by:by,path:path,cents:parentC,amount:parentC/100,n:ids.size,
    splits:tiles.length>=2||(tiles.length===1&&tiles[0].drillable),tiles:tiles};
}

// What the explorer shows for a path → {level, selected, total}:
//   • the level below it while there is something to drill into;
//   • otherwise (a vendor, a tile with one thing all the way down, or
//     nothing in view any more) the tile's own level with it `selected` —
//     its siblings faded, still one click away. `total` is always the
//     path's own level (its $ and purchases).
function explorerView(list,path,partsOf){
  path=path||[];
  var cur=explorerLevel(list,path,partsOf);
  if(!path.length||cur.splits)return{level:cur,selected:null,total:cur};
  var last=path[path.length-1];
  var sib=explorerLevel(list,path.slice(0,-1),partsOf,{by:last.by});
  return{level:sib,selected:last.key,total:cur};
}

// ── Paths ────────────────────────────────────────────────────

// "Marketing · Paper @ Staples", "Hardware @ Best Buy", "Software · Claude".
function pathLabel(path){
  var out=[];
  (path||[]).forEach(function(e,i){
    var l=e.label||labelOf(e.by,e.key);
    if(e.by==="vendor"&&i>0&&out.length)out[out.length-1]+=" @ "+l;else out.push(l);
  });
  return out.join(" · ");
}
function samePath(a,b){
  a=a||[];b=b||[];
  if(a.length!==b.length)return false;
  for(var i=0;i<a.length;i++)if(a[i].by!==b[i].by||a[i].key!==b[i].key)return false;
  return true;
}
// The dimension the level below `path` is drawn in over `list` (null below
// the last level).
function levelDim(list,path,partsOf){
  path=path||[];partsOf=partsOf||expenseItemParts;
  if(path.length>=MAX_DEPTH)return null;
  var items=[];
  (list||[]).forEach(function(ex){if(ex)partsOf(ex).forEach(function(p){if(partMatchesPath(ex,p,path))items.push({ex:ex,p:p});});});
  return dimFor(path.length,path,items);
}
function groupPath(g){return[{by:"group",key:g,label:g}];}
// The path to a part's level-2 tile: its group, then its key in `by` (the
// dimension the explorer draws that group in — levelDim).
function itemPath(ex,p,by){
  var top=groupPath(groupOfPart(ex,p));
  by=by||dimFor(1,top,[{ex:ex,p:p}]);
  var k=keyOf(by,ex,p);
  return top.concat([{by:by,key:k,label:labelOf(by,k)}]);
}
// The same, working the dimension out over `list` (the log's Item chip).
function pathForPart(list,ex,p,partsOf){
  return itemPath(ex,p,levelDim(list,groupPath(groupOfPart(ex,p)),partsOf));
}
// A path from plain keys ("Marketing", "Paper", "Staples"), each level drawn
// as the explorer would draw it over `list`. null when a key isn't a tile.
function tileNamed(tiles,k){
  for(var j=0;j<tiles.length;j++)if(tiles[j].key===k||tiles[j].label===k)return tiles[j];
  return null;
}
function pathTo(list,keys,partsOf){
  var path=[];
  for(var i=0;i<(keys||[]).length;i++){
    var t=tileNamed(explorerLevel(list,path,partsOf).tiles,keys[i]);
    if(!t)return null;
    path=t.path;
  }
  return path;
}

export {
  GROUPS, GROUP_NOTE, MAX_DEPTH, OTHER_ITEMS,
  groupOfPart, keyOf, partMatchesPath, rowMatchesPath, explorerLevel, explorerView, levelDim,
  pathLabel, samePath, groupPath, itemPath, pathForPart, pathTo
};
